<template>
  <div class="nx-clipping-selector-wrapper" v-resize="onResize">

    <label v-if="label" class="primary--text" :class="{ 'error--text': hasErrors }">
      {{ label }}
    </label>
    <div class="nx-clipping-selector">
      <v-card>

        <nx-image
          class="image grey lighten-3"
          :src="currentClipping.src"
          :width="currentClipping.image ? currentClipping.image.width : null"
          height="160"
          :alt="currentClipping.image ? currentClipping.image.title : null"
          v-if="hasImage"
          v-bind="$attrs"
        ></nx-image>

        <div
          v-else
          class="image-placeholder grey lighten-3 grey--text"
        >
          <p class="text" v-show="!loading">{{ $t('message.clipping.selector.noimage') }}</p>
          <p class="text" v-show="loading">
            {{ $t('message.clipping.selector.loading') }}
            <br>
            <v-progress-circular
              indeterminate
              color="secondary"
            ></v-progress-circular>
          </p>
        </div>

        <v-card-actions>
          <v-layout :column="xs">
            <v-flex class="text-xs-right">
              <nx-content-selector
                :error-messages="errorMessages"
                v-if="!disableImageSelector"
                :label="label"
                :type="contentSelectorType"
                :value="contentSelectorValue"
                :disabled="disabled"
                :additional-filters="computedAdditionalFilters"
                :allowNonPublished="allowNonPublished"
                @input="contentSelected"
                @focus="$emit('focus')"
                @blur="$emit('blur')"
              >
                <template slot="append-outer">
                  <v-tooltip top class="text-xs-center">
                    <v-btn
                      v-if="!disableImageUpload"
                      icon
                      slot="activator"
                      :disabled="disabled"
                      @click="showWizard = true"
                    >
                      <v-icon>file_upload</v-icon>
                    </v-btn>
                    <span>{{ $t('message.clipping.selector.upload')}}</span>
                  </v-tooltip>
                  <v-tooltip top class="text-xs-center">
                    <v-btn
                      v-if="enableImageCrop"
                      class="ml-1"
                      :disabled="!hasImage || disabled"
                      icon
                      slot="activator"
                      @click="openCroppingTool()"
                    >
                      <v-icon>crop</v-icon>
                    </v-btn>
                    <span>{{ $t('message.clipping.selector.crop')}}</span>
                  </v-tooltip>
                </template>
              </nx-content-selector>
              <template v-else>
                <v-tooltip top class="text-xs-center">
                  <v-btn
                    v-if="enableImageCrop"
                    class="ml-1"
                    :disabled="!hasImage || disabled"
                    icon
                    slot="activator"
                    @click="openCroppingTool()"
                  >
                    <v-icon>crop</v-icon>
                  </v-btn>
                  <span>{{ $t('message.clipping.selector.crop')}}</span>
                </v-tooltip>
              </template>
            </v-flex>

          </v-layout>
        </v-card-actions>
      </v-card>
    </div>
    <nx-entity-wizard
      entity="image"
      :active="showWizard"
      :before-create-clipping-aliases="beforeCreateClippingAliases"
      @dismiss="showWizard = false"
      @entityCreated="entityCreated">
    </nx-entity-wizard>

    <nx-clipping-tool
      :active="croppingToolState.active"
      :aspectRatios="aspectRatios"
      :value="[currentClipping]"
      @dismiss="closeCroppingTool()"
      @input="acceptClippingTool"
      title="Realizar recorte"
      v-if="croppingToolState.render"
      :allow-only-source-aspect-ratio="allowOnlySourceAspectRatio"
    ></nx-clipping-tool>

  </div>
</template>

<script>
import Loadable from '@/mixins/loadable';
import NxImage from '@/components/media/Image';
import NxClippingTool from '@/modules/clipping/Tool';
import NxContentSelector from '@/modules/content/selector/Selector';
import isObject from 'lodash/isObject';
import features from '@/features';

import { aspectRatios } from './config';
import api from './api';

export default {
  name: 'nx-clipping-selector',
  mixins: [Loadable],
  inject: {
    $getParentEditor: {
      default: null,
    },
  },
  components: {
    NxImage,
    NxClippingTool,
    NxContentSelector,
    // fixme: dependencia circular
    NxEntityWizard: () => import('@/modules/entity/wizard/Wizard'),
  },
  props: {
    allowOnlySourceAspectRatio: Boolean,
    allowNonPublished: {
      type: Boolean,
      default: false,
    },
    aspectRatios: {
      type: Array,
      default: () => ([aspectRatios['16-9-aspect-ratio'].alias]),
      required: true,
    },
    beforeCreateClippingAliases: {
      type: Array,
      default: () => ([]),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    disableCrop: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: null,
    },
    titleLength: {
      type: [Number],
      default: 0,
    },
    /** @type ClippingCard|String */
    value: {
      type: [Object, String],
      default: null,
    },
    /*
    * Example: [
    *   {
    *     fromField: 'contentId'
    *     toFilter: 'contentId'
    *   }
    * ]
    */
    additionalFilters: {
      type: Object,
      default: () => ({}),
    },

    additionalFiltersFromFormFields: {
      type: Array,
      default: () => ([]),
    },
    errorMessages: {
      type: Array,
      default: () => ([]),
    },
    disableImageSelector: {
      type: Boolean,
      default: false,
    },
    disableImageUpload: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      clippingValue: null,
      openFileUpload: false,
      croppingToolState: {
        active: false,
        render: false,
      },
      xs: false,
      showWizard: false,
    };
  },
  computed: {
    computedAdditionalFilters() {
      if (!this.$getParentEditor) {
        return this.additionalFilters;
      }
      const editor = this.$getParentEditor && this.$getParentEditor();
      const filtersFromFormFields = {};
      this.additionalFiltersFromFormFields.forEach((config) => {
        const filterValue = editor.draft[config.fromField];
        if (!filterValue) {
          return;
        }
        filtersFromFormFields[config.toFilter] = filterValue;
      });

      return {
        ...this.additionalFilters,
        ...filtersFromFormFields,
      };
    },
    enableImageCrop() {
      return !(this.disableCrop || features.DISABLE_IMAGE_CROP_IN_UPLOADED_IMAGES);
    },
    currentClipping() {
      return isObject(this.value) ? this.value : this.clippingValue;
    },
    currentClippingId() {
      return this.currentClipping.id;
    },
    hasImage() {
      return this.currentClipping ? this.currentClipping.src : false;
    },
    hasErrors() {
      return this.errorMessages.length > 0;
    },
    contentSelectorValue() {
      return this.currentClipping ? this.currentClipping.image : null;
    },
    contentSelectorType() {
      // TODO, sacar de una prop
      return ['image'];
    },
    allowedAspectRatios() {
      return this.onlySourceAspectRatioIsAllowed
        ? [aspectRatios['source-aspect-ratio'].alias]
        : this.aspectRatios;
    },
    onlySourceAspectRatioIsAllowed() {
      return this.allowOnlySourceAspectRatio
        || (this.aspectRatios.length === 1
          && this.aspectRatios.includes(aspectRatios['source-aspect-ratio'].alias));
    },
    optionsClasses() {
      if (this.xs) {
        return {
          xs12: true,
          'd-flex': true,
          'pt-0': true,
          'justify-space-around': true,
        };
      }
      return {
        'd-flex': true,
        'align-center': true,
        'justify-center': true,
        'pl-0': true,
      };
    },
  },
  methods: {
    onResize() {
      this.xs = this.$el && this.$el.clientWidth <= 320;
    },
    async clear() {
      this.$emit('input', null);
    },
    async fetchClipping(clippingId) {
      this.loadingStart();
      this.clippingValue = await api.getClipping(clippingId);
      this.loadingFinish();
    },
    /** BEGIN - Content selector methods * */
    async contentSelected(content) {
      if (!content) {
        return this.clear();
      }
      this.loadingStart();
      const imageClippings = await api.clippingSearch(content, this.allowedAspectRatios);
      this.loadingFinish();
      return this.$emit('input', imageClippings[0]);
    },
    /** END - Content selector methods * */
    /** BEGIN - Image wizard methods * */
    openFileUploadDialog() {
      this.openFileUpload = true;
    },
    entityCreated(entity) {
      delete entity.route;
      this.contentSelected(entity);
      this.$emit('entityCreated', entity);
    },
    /** END - Image wizard methods * */
    /** BEGIN - Crop Tool methods * */
    openCroppingTool() {
      this.croppingToolState.render = true;
      this.$nextTick(() => {
        this.croppingToolState.active = true;
      });
    },
    closeCroppingTool() {
      this.croppingToolState.active = false;
      this.$nextTick(() => {
        this.croppingToolState.render = false;
      });
    },
    async acceptClippingTool(clipping) {
      return this.$emit('input', clipping[0]);
    },
    /** END - Crop Tool methods * */
  },
  watch: {
    value: {
      async handler(value) {
        if (!value) {
          this.clippingValue = null;
          return;
        }

        if (isObject(value)) {
          this.clippingValue = value;
          return;
        }

        await this.fetchClipping(value);
      },
      immediate: true,
    },
  },
  mounted() {
    this.onResize();
  },
};
</script>


<style lang="stylus">
.nx-clipping-selector-wrapper {
  margin-bottom: 20px;
  min-width: 220px;

  label {
    font-size: 12px;
  }
}

.nx-clipping-selector {
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .image-placeholder {
    animation: fadeIn 1s;
    display: flex;
    padding-top: 22%;
    align-items: center;
    align-content: center;
    justify-content: center;
    height: 150px;

    .text {
      text-align: center;
      position: relative;
      margin-top: -22%;
      animation: fadeIn 1s;
    }
  }

  .nx-content-selector {
  }
}

.nx-form--sidebar {
  .nx-clipping-selector {
    .nx-chip.v-chip .v-chip__content > * {
      max-width: 100px;
    }
  }
}
</style>
